import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import styles from './InfoTray.module.css';

interface InfoTrayProps {
  open: boolean;
  onClose: () => void;
  /** Primary heading — usually the indicator or section name. */
  title: ReactNode;
  /** Optional small label above the title (e.g. "Section" / "Indicator"). */
  eyebrow?: ReactNode;
  children: ReactNode;
}

/**
 * Info tray.
 *
 * Generalizes the Lens "Performance Domains" legend (`#lens-legend`) into a
 * reusable surface so every info button in the app opens the same affordance:
 * a slide-up bottom sheet on mobile (covering the BottomNav, as a modal sheet
 * should) and a centered card on desktop, both with a grab handle, dimmed
 * backdrop, and Escape-to-close.
 *
 * Structure: a sticky header (grip, eyebrow, title, close) and a scrolling
 * body, so a long explanation scrolls under a fixed title instead of the
 * whole sheet scrolling the heading away. Rendered in a portal so an
 * ancestor's transform / overflow (e.g. a Modal panel) can never clip or
 * stack over it.
 */
export function InfoTray({ open, onClose, title, eyebrow, children }: InfoTrayProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Information'}
        className={styles.tray}
      >
        <header className={styles.header}>
          <span className={styles.grip} aria-hidden="true" />
          <div className={styles.headingRow}>
            <div className={styles.headingWrap}>
              {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
              <h3 className={styles.title}>{title}</h3>
            </div>
            <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
              <svg
                viewBox="0 0 16 16"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>
        </header>
        <div className={styles.body}>{children}</div>
      </div>
    </>,
    document.body,
  );
}
